import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBrand, pubFromHostname, HEROES, HeroPicture } from "../lib/brand";
import { api, clearSubmitterToken, getSubmitterToken } from "../lib/api";

/**
 * Public page header: full-width hero image per publication (neutral when no
 * publication is known), HOME top left, white LOGIN pill top right, centred
 * publication logo (DWH/DWT only), BEHIND THE LOVE + subtitle.
 */
export default function PublicHeader({ pub = null }) {
  const [brand, setBrand] = useState({});
  const [submitter, setSubmitter] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let live = true;
    getBrand().then((b) => { if (live) setBrand(b || {}); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!getSubmitterToken()) return undefined;
    let live = true;
    api.get("/my/account")
      .then(({ data }) => { if (live) setSubmitter(data); })
      .catch(() => { clearSubmitterToken(); });
    return () => { live = false; };
  }, []);

  const signOut = async () => {
    try { await api.post("/auth/submitter/logout"); } catch { /* clear local session regardless */ }
    clearSubmitterToken();
    setSubmitter(null);
    navigate("/create-event");
  };

  const effectivePub = pub || pubFromHostname(brand);
  const known = effectivePub === "dwh" || effectivePub === "dwt";
  const hero = known ? HEROES[effectivePub] : HEROES.neutral;
  const darkText = !known; // neutral hero is a bright image -> dark text
  const logo = known ? brand[`${effectivePub}_logo_url`] : "";

  return (
    <header className="relative w-full overflow-hidden" data-testid="brand-header" data-pub={effectivePub || "neutral"}>
      <HeroPicture
        base={hero}
        className="absolute inset-0 w-full h-full object-cover"
        testId="hero-banner-image"
      />
      {/* scrim keeps the white overlay text readable on the DWT hero */}
      {known && <div className="absolute inset-0 bg-black/25" />}
      <div className="relative h-[240px] sm:h-[300px] flex flex-col">
        <div className="flex items-start justify-between px-5 sm:px-8 pt-5">
          <Link
            to="/create-event"
            data-testid="header-home-link"
            className={`text-xs font-bold uppercase tracking-[0.2em] ${darkText ? "text-black" : "text-white"}`}
            style={darkText ? {} : { textShadow: "0 1px 4px rgba(0,0,0,0.45)" }}
          >
            Home
          </Link>
          {submitter ? (
            <details className="relative" data-testid="submitter-account-menu">
              <summary className="list-none cursor-pointer bg-white text-black text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full border border-black/10 max-w-[220px] truncate">
                {submitter.email}
              </summary>
              <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#c19872] shadow-lg z-30 p-2 text-black">
                <p className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#5c5852]">Signed in as</p>
                <p className="px-3 pb-2 text-xs break-all">{submitter.email}</p>
                <Link to="/my-submissions" data-testid="header-my-submissions" className="block px-3 py-2 text-sm hover:bg-[#f5f3ed]">My submissions</Link>
                <button type="button" onClick={signOut} data-testid="header-signout" className="w-full text-left px-3 py-2 text-sm hover:bg-[#f5f3ed]">Sign out</button>
              </div>
            </details>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="bg-white text-black text-xs font-bold uppercase tracking-[0.2em] px-5 py-2 rounded-full border border-black/10 hover:opacity-85 transition-opacity"
            >
              Login
            </Link>
          )}
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-4">
          {known && logo && (
            <img
              src={logo}
              alt={effectivePub === "dwh" ? "Dancing With Her" : "Dancing With Them"}
              className="h-10 sm:h-14 w-auto object-contain brightness-0 invert mb-3"
              data-testid="header-logo"
            />
          )}
          <p
            data-testid="header-title"
            className={`text-lg sm:text-xl font-bold uppercase tracking-[0.25em] ${darkText ? "text-black" : "text-white"}`}
            style={darkText ? {} : { textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
          >
            Behind the Love
          </p>
          <p
            data-testid="header-subtitle"
            className={`text-xs sm:text-sm mt-2 ${darkText ? "text-black/80" : "text-white/95"}`}
            style={darkText ? {} : { textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            Tell your story and credit the creatives who brought it to life
          </p>
        </div>
      </div>
    </header>
  );
}
